import type { FC } from "react";

import moment from "moment";
import { useHistory } from "react-router";

import { Button, Icon, Text } from "@alpacahq/ui/old";

import type { OptionsApprovalStatus } from "~/v2/api/rest/account";
import { OPTIONS_TIERS } from "~/v2/constants";
import { Path } from "~/v2/path";
import {
  OPTIONS_DOCS_URL,
  OPTIONS_LEARN_URL,
  OPTIONS_YOUTUBE_URL,
} from "../../constant";

type StatusProps = {
  status: OptionsApprovalStatus;
};

export const ApplicationStatus: FC<StatusProps> = ({ status }) => {
  const history = useHistory();

  const onDocsClicked = () => window.open(OPTIONS_DOCS_URL, "_blank");
  const onLearnClicked = () => window.open(OPTIONS_LEARN_URL, "_blank");
  const onYouTubeClicked = () => window.open(OPTIONS_YOUTUBE_URL, "_blank");
  const onBackClicked = () => history.push(Path.ROUTE_DASHBOARD);

  const isRequestedLevelApproved =
    status.ApprovedLevel &&
    status.RequestedLevel &&
    (status.OptionsRequestStatus === "APPROVED" ||
      status.OptionsRequestStatus === "LOWER_LEVEL_APPROVED") &&
    status.ApprovedLevel === status.RequestedLevel;

  const isLowerApproved = status.ApprovedLevel && status.ApprovedLevel > 0;

  return (
    <div className="w-full max-w-3xl px-4 pt-2">
      <h2 className="border-gray-200 border-b pb-4 font-bold text-2xl">
        {isRequestedLevelApproved
          ? "Application Approved"
          : "Application Received"}
      </h2>

      {!isRequestedLevelApproved && !isLowerApproved ? (
        <Text className="mt-4 font-medium text-lg">
          Please wait 1 - 2 business days for us to review your application.
          <br />
          You will be notified when your application has been reviewed.
        </Text>
      ) : (
        <div className="mt-4">
          <Text className="font-medium text-lg">
            {isRequestedLevelApproved
              ? `You're all set to start trading options! You've been approved for Level ${status.ApprovedLevel} Options Trading:`
              : `You're approved for Level ${
                  status.ApprovedLevel
                } Options Trading. If you've applied for Level ${
                  status.RequestedLevel
                }, and did not meet the criteria, your account can be reevaluated on ${moment(
                  status.ReapplyAvailableDate,
                ).format("MMMM Do")}.`}
          </Text>

          {Object.entries(OPTIONS_TIERS)
            .filter(([level]) => Number(level) <= (status.ApprovedLevel || 0))
            .map(([level, items]) => (
              <div className="mt-4 space-y-2" key={level}>
                <Text>Level {level}</Text>
                {Number(level) > 1 && (
                  <Text>Everything in Level {Number(level) - 1} plus:</Text>
                )}
                <ul>
                  {items.map((item) => (
                    <li key={item} className="flex items-center">
                      <Icon
                        name="CheckBadge"
                        variant="filled"
                        className="text-yellow-400"
                      />
                      <Text className="ml-2">{item}</Text>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

          <Text className="mt-4 font-medium text-lg">
            Search for your favorite symbol and start trading now!
          </Text>
        </div>
      )}

      <div className="mt-4 space-y-4 rounded border border-gray-20 bg-cool-gray-50 px-4 py-8">
        <Text className="text-gray-900 text-lg">Want to learn more?</Text>
        <div>
          <Text className="font-medium">Learn</Text>
          <div className="flex items-center justify-between gap-4 pt-2">
            <Text className="text-gray-700">
              Check out our Learn page for more in depth articles on options
              strategies, options variables and more.
            </Text>
            <Button
              onClick={onLearnClicked}
              className="min-w-[170px] border border-yellow-300 bg-yellow-50 text-yellow-900"
            >
              Learn <Icon name="ArrowRight" className="ml-2 text-yellow-900" />
            </Button>
          </div>
        </div>
        <div>
          <Text className="font-medium">YouTube</Text>
          <div className="flex items-center justify-between gap-4 pt-2">
            <Text className="text-gray-700">
              Learn how to configure your environment, create orders, and
              monitor your orders, trades and positions with Alpaca's Trading
              API.
            </Text>
            <Button
              onClick={onYouTubeClicked}
              className="min-w-[170px] border border-yellow-300 bg-yellow-50 text-yellow-900"
            >
              Watch Tutorial{" "}
              <Icon name="ArrowRight" className="ml-2 text-yellow-900" />
            </Button>
          </div>
        </div>
        <div>
          <Text className="font-medium">API Reference</Text>
          <div className="flex items-center justify-between gap-4 pt-2">
            <Text className="text-gray-700">
              Check out our reference documentation for Trading API, including
              examples and code snippets for our endpoints in Node.js, Ruby,
              PHP, Python and more.
            </Text>
            <Button
              onClick={onDocsClicked}
              className="min-w-[170px] border border-yellow-300 bg-yellow-50 text-yellow-900"
            >
              API Reference{" "}
              <Icon name="ArrowRight" className="ml-2 text-yellow-900" />
            </Button>
          </div>
        </div>
      </div>

      <Button
        onClick={onBackClicked}
        variant="secondary"
        className="mt-8 ml-auto"
      >
        Back to Dashboard
      </Button>
    </div>
  );
};
